import { GAME_KEYS } from '@/lib/models';

/**
 * The navigation tree.
 *
 * A game's section appears only when the account has a save in it, which is why the
 * menu key and the route prefix both live here. They differ for CHUNITHM and that
 * asymmetry is load-bearing: `/api/user/me` reports `chusan` while the routes are
 * mounted under `/chuni/v2`. Reconciling them would mean changing the API.
 */
export interface MenuEntry {
  /** Route path, absolute. */
  to: string;
  /** i18n key. */
  title: string;
}

export interface GameSection {
  /** The string `user.games` uses. */
  key: string;
  /** The route prefix, which is not always the key. */
  prefix: string;
  /** Drives the accent colour via `data-game`. */
  game: 'chuni' | 'mai2' | 'ongeki';
  title: string;
  entries: MenuEntry[];
}

const chuni: GameSection = {
  key: GAME_KEYS.chuni,
  prefix: '/chuni/v2',
  game: 'chuni',
  title: 'Chuni.Title',
  entries: [
    { to: '/chuni/v2/profile', title: 'Common.Profile' },
    { to: '/chuni/v2/rating', title: 'Common.Rating' },
    { to: '/chuni/v2/recent', title: 'Common.PlayRecord' },
    { to: '/chuni/v2/song', title: 'Common.MusicList' },
    { to: '/chuni/v2/character', title: 'Chuni.Character' },
    { to: '/chuni/v2/userbox', title: 'Chuni.UserBox' },
    { to: '/chuni/v2/rival', title: 'Common.Rival' },
    { to: '/chuni/v2/userRanking', title: 'Common.UserRanking' },
    { to: '/chuni/v2/setting', title: 'Common.Setting' },
  ],
};

const mai2: GameSection = {
  key: GAME_KEYS.mai2,
  prefix: '/mai2',
  game: 'mai2',
  title: 'Mai2.Title',
  entries: [
    { to: '/mai2/profile', title: 'Common.Profile' },
    { to: '/mai2/rating', title: 'Common.Rating' },
    { to: '/mai2/recent', title: 'Common.PlayRecord' },
    { to: '/mai2/songlist', title: 'Common.MusicList' },
    { to: '/mai2/photos', title: 'Mai2.Photos' },
    { to: '/mai2/dxpass', title: 'Mai2.DxPass' },
    { to: '/mai2/circle', title: 'Mai2.Circle' },
    { to: '/mai2/festa', title: 'Mai2.Festa' },
    { to: '/mai2/servermissions', title: 'Mai2.ServerMissions' },
    { to: '/mai2/pointexchanges', title: 'Mai2.PointExchanges' },
    { to: '/mai2/rival', title: 'Common.Rival' },
    { to: '/mai2/setting', title: 'Common.Setting' },
  ],
};

const ongeki: GameSection = {
  key: GAME_KEYS.ongeki,
  prefix: '/ongeki',
  game: 'ongeki',
  title: 'Ongeki.Title',
  entries: [
    { to: '/ongeki/profile', title: 'Common.Profile' },
    { to: '/ongeki/rating', title: 'Common.Rating' },
    { to: '/ongeki/recent', title: 'Common.PlayRecord' },
    { to: '/ongeki/song', title: 'Common.MusicList' },
    { to: '/ongeki/battle', title: 'Ongeki.Battle' },
    { to: '/ongeki/card', title: 'Ongeki.Card' },
    { to: '/ongeki/card/gallery', title: 'Ongeki.CardGallery' },
    { to: '/ongeki/musicRanking', title: 'Ongeki.MusicRanking' },
    { to: '/ongeki/userRanking', title: 'Common.UserRanking' },
    { to: '/ongeki/rival', title: 'Common.Rival' },
    { to: '/ongeki/settings', title: 'Common.Setting' },
  ],
};

export const GAME_SECTIONS: readonly GameSection[] = [chuni, mai2, ongeki];

/** Account pages, shown to anyone signed in. */
export const ACCOUNT_ENTRIES: readonly MenuEntry[] = [
  { to: '/dashboard', title: 'Nav.Dashboard' },
  { to: '/profile', title: 'Nav.Account' },
  { to: '/cards', title: 'Nav.Cards' },
  { to: '/keychip', title: 'Nav.Keychip' },
  { to: '/import', title: 'Nav.Import' },
  { to: '/announcements', title: 'Nav.Announcements' },
  // Shown to everyone; the page itself says so when the role is missing, and the
  // server refuses every admin endpoint regardless. Hiding it here would only mean
  // an operator cannot find their own tools.
  { to: '/admin', title: 'Admin.Title' },
];

/** Which game section a path belongs to, for the `data-game` accent. */
export function sectionForPath(pathname: string): GameSection | null {
  return GAME_SECTIONS.find((section) => pathname.startsWith(section.prefix)) ?? null;
}
